import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import axios from 'axios'
import { useToast } from '../hooks/useToast'

interface ClientDashboardStats {
  totalTickets: number
  openTickets: number
  closedTickets: number
  avgResolutionTime: number
  activeServices: number
  recentActivity: RecentActivity[]
  serviceUsage: ServiceUsage[]
  upcomingBilling: UpcomingBilling[]
}

interface RecentActivity {
  id: string
  type: 'ticket_created' | 'ticket_updated' | 'comment_added' | 'status_changed' | 'service_renewed'
  title: string
  description: string
  timestamp: string
  ticketId?: string
  serviceId?: string
}

interface ServiceUsage {
  serviceId: string
  serviceName: string
  serviceType: string
  currentCycle: number
  totalCycles: number
  daysRemaining: number
  usagePercentage: number
  nextBillingDate: string
  status: string
}

interface UpcomingBilling {
  serviceId: string
  serviceName: string
  amount: string
  dueDate: string
  daysUntilDue: number
}

const ClientDashboard: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { showError, showAuthError } = useToast()
  
  const [stats, setStats] = useState<ClientDashboardStats>({
    totalTickets: 0,
    openTickets: 0,
    closedTickets: 0,
    avgResolutionTime: 0,
    activeServices: 0,
    recentActivity: [],
    serviceUsage: [],
    upcomingBilling: []
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchClientDashboard = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Fetch comprehensive client dashboard data
        const response = await axios.get('/api/client/dashboard')
        
        if (response.data.success) {
          setStats(response.data.data)
        } else {
          setError(response.data.error || 'Failed to fetch dashboard data')
        }
      } catch (error: any) {
        console.error('Error fetching client dashboard:', error)
        
        // Handle authentication errors specifically
        if (error.response?.status === 401 || error.response?.status === 403) {
          showAuthError('Please log in again to access your dashboard')
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login')
          }, 2000)
        } else {
          setError(error.response?.data?.error || 'Failed to fetch dashboard data')
          showError('Unable to load dashboard data. Please try refreshing the page.')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchClientDashboard()
  }, [])

  const formatHours = (hours: number) => {
    if (hours === 0) return '0h'
    if (hours < 1) return '<1h'
    if (hours < 24) return `${Math.round(hours)}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    if (remainingHours === 0) return `${days}d`
    return `${days}d ${Math.round(remainingHours)}h`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getActivityIcon = (type: string) => {
    const icons = {
      ticket_created: '📝',
      ticket_updated: '✏️',
      comment_added: '💬',
      status_changed: '🔄',
      service_renewed: '🔄'
    }
    return icons[type as keyof typeof icons] || '📋'
  }

  const getStatusColor = (status: string) => {
    const colors = {
      'active': 'bg-green-100 text-green-800',
      'suspended': 'bg-yellow-100 text-yellow-800',
      'cancelled': 'bg-red-100 text-red-800',
      'expired': 'bg-gray-100 text-gray-800'
    }
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800'
  }

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-red-900">Error loading dashboard</h3>
          <p className="mt-2 text-sm text-red-500">{error}</p>
          <button 
            className="mt-4 btn-primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Client Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Welcome back! Here's your personalized overview of services and support
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <button
            onClick={() => navigate('/tickets/new')}
            className="btn-primary"
          >
            New Support Ticket
          </button>
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/tickets')}>
          <h3 className="text-lg font-medium text-gray-900">Total Tickets</h3>
          <p className="mt-2 text-3xl font-bold text-primary-600">{stats.totalTickets}</p>
          <p className="mt-1 text-sm text-gray-500">All time</p>
        </div>
        
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/tickets')}>
          <h3 className="text-lg font-medium text-gray-900">Active Tickets</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">{stats.openTickets}</p>
          <p className="mt-1 text-sm text-gray-500">Currently open</p>
        </div>
        
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/services')}>
          <h3 className="text-lg font-medium text-gray-900">Active Services</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{stats.activeServices}</p>
          <p className="mt-1 text-sm text-gray-500">Your subscriptions</p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Response Time</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">{formatHours(stats.avgResolutionTime)}</p>
          <p className="mt-1 text-sm text-gray-500">Average resolution</p>
        </div>
      </div>

      {/* Service Usage Overview */}
      {stats.serviceUsage.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Service Usage</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {stats.serviceUsage.map((service) => (
              <div key={service.serviceId} className="card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-medium text-gray-900">{service.serviceName}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                    {service.status.charAt(0).toUpperCase() + service.status.slice(1)}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Billing Cycle</span>
                      <span>{service.currentCycle} of {service.totalCycles}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${service.overallUsagePercentage ?? service.usagePercentage}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Days Remaining:</span>
                      <p className="font-medium text-gray-900">{service.daysRemaining}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Next Billing:</span>
                      <p className="font-medium text-gray-900">{formatDate(service.nextBillingDate)}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => navigate(`/services`)}
                    className="w-full btn-secondary text-sm"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {stats.recentActivity.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="card">
            <div className="space-y-4">
              {stats.recentActivity.slice(0, 5).map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="text-2xl">{getActivityIcon(activity.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                    <p className="text-sm text-gray-600">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-1">{formatDate(activity.timestamp)}</p>
                  </div>
                  {activity.ticketId && (
                    <button
                      onClick={() => navigate(`/tickets/${activity.ticketId}`)}
                      className="btn-secondary text-xs"
                    >
                      View
                    </button>
                  )}
                </div>
              ))}
            </div>
            {stats.recentActivity.length > 5 && (
              <div className="border-t border-gray-200 pt-4 text-center">
                <button
                  onClick={() => navigate('/tickets')}
                  className="btn-secondary text-sm"
                >
                  View All Activity
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upcoming Billing */}
      {stats.upcomingBilling.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Billing</h2>
          <div className="card">
            <div className="space-y-3">
              {stats.upcomingBilling.map((billing) => (
                <div key={billing.serviceId} className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{billing.serviceName}</h3>
                    <p className="text-sm text-gray-600">
                      Due in {billing.daysUntilDue} days • {formatDate(billing.dueDate)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">{billing.amount}</p>
                    <p className="text-xs text-gray-500">Due Soon</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <button
            onClick={() => navigate('/tickets/new')}
            className="card hover:bg-gray-50 transition-colors text-center p-6"
          >
            <div className="text-3xl mb-2">📝</div>
            <h3 className="font-medium text-gray-900">New Ticket</h3>
            <p className="text-sm text-gray-600 mt-1">Create support request</p>
          </button>
          
          <button
            onClick={() => navigate('/services')}
            className="card hover:bg-gray-50 transition-colors text-center p-6"
          >
            <div className="text-3xl mb-2">🔧</div>
            <h3 className="font-medium text-gray-900">My Services</h3>
            <p className="text-sm text-gray-600 mt-1">View subscriptions</p>
          </button>
          
          <button
            onClick={() => navigate('/knowledge-base')}
            className="card hover:bg-gray-50 transition-colors text-center p-6"
          >
            <div className="text-3xl mb-2">📚</div>
            <h3 className="font-medium text-gray-900">Knowledge Base</h3>
            <p className="text-sm text-gray-600 mt-1">Find answers</p>
          </button>
          
          <button
            onClick={() => navigate('/tickets')}
            className="card hover:bg-gray-50 transition-colors text-center p-6"
          >
            <div className="text-3xl mb-2">📋</div>
            <h3 className="font-medium text-gray-900">All Tickets</h3>
            <p className="text-sm text-gray-600 mt-1">View history</p>
          </button>
        </div>
      </div>
    </div>
  )
}

export default ClientDashboard
